import type { ComponentDefinition, ComponentField } from '@/types/reports';
import { hasValidHTMLContent } from '@/utils/html-content';
import { isValidReportDateString } from '@/pages/reports/components/form/reportDateUtils';

const isValidUrl = (value: string): boolean => {
  const val = (value ?? '').trim();
  if (!val) return false;
  try {
    const toParse = /^www\./i.test(val) ? `https://${val}` : val;
    const url = new URL(toParse);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

export function validateComponentProps(
  props: Record<string, any>,
  componentDef: ComponentDefinition | null
): Record<string, string> {
  if (!componentDef) return {};

  const errors: Record<string, string> = {};

  componentDef.fields.forEach((field: ComponentField) => {
    const value = props[field.key];

    if (field.type === 'date') {
      const val = (value ?? '').toString().trim();
      if (val && !isValidReportDateString(val)) {
        errors[field.key] = `${field.label} deve ser uma data válida`;
        return;
      }
    }

    if (field.type === 'url' || field.type === 'url-text' || field.type === 'image') {
      const val = (value ?? '').toString().trim();
      if (val && !isValidUrl(val)) {
        errors[field.key] = `${field.label} deve ser uma URL válida`;
        return;
      }
    }

    if (field.type === 'links') {
      const links = value || [];
      if (
        field.required &&
        (!Array.isArray(links) ||
          links.length === 0 ||
          links.some((link: any) => !link.label?.trim() || !link.url?.trim()))
      ) {
        errors[field.key] = `${field.label} é obrigatório`;
        return;
      }

      if (
        Array.isArray(links) &&
        links.some((link: any) => link.url && !isValidUrl(String(link.url).trim()))
      ) {
        errors[field.key] = `${field.label} deve conter URLs válidas`;
        return;
      }
    } else if (field.type === 'logo-links') {
      const logos = value || [];

      if (field.required && (!Array.isArray(logos) || logos.length === 0)) {
        errors[field.key] = `${field.label} é obrigatório`;
        return;
      }

      if (
        Array.isArray(logos) &&
        logos.some((item: any) => item.imageUrl && !isValidUrl(String(item.imageUrl).trim()))
      ) {
        errors[field.key] = `${field.label} deve conter URLs de imagem válidas`;
        return;
      }

      if (
        Array.isArray(logos) &&
        logos.some((item: any) => item.link && !isValidUrl(String(item.link).trim()))
      ) {
        errors[field.key] = `${field.label} deve conter URLs válidas`;
        return;
      }
    } else if (field.type === 'creator-entries' || field.type === 'brand-entries') {
      const entries = value || [];

      if (Array.isArray(entries) && entries.length > 0) {
        const hasInvalidImageUrl = entries.some((item: any) => {
          const url = (item.imageUrl ?? '').toString().trim();
          return !url || !isValidUrl(url);
        });

        if (hasInvalidImageUrl) {
          errors[field.key] = `${field.label} deve conter URLs de imagem válidas`;
          return;
        }

        const hasInvalidLink = entries.some((item: any) => {
          const url = (item.link ?? '').toString().trim();
          return !url || !isValidUrl(url);
        });

        if (hasInvalidLink) {
          errors[field.key] = `${field.label} deve conter URLs válidas`;
          return;
        }
      }
    } else if (field.type === 'curatorship-items') {
      const items = value || [];
      if (!Array.isArray(items)) return;

      const partiallyFilled = items.filter((item: any) => {
        const title = (item?.title ?? '').toString().trim();
        const url = (item?.url ?? '').toString().trim();
        return (title && !url) || (!title && url);
      });

      if (partiallyFilled.length > 0) {
        errors[field.key] = `${field.label}: cada item precisa de título e URL juntos`;
        return;
      }

      if (
        Array.isArray(items) &&
        items.some((item: any) => {
          const url = (item?.url ?? '').toString().trim();
          return url && !isValidUrl(url);
        })
      ) {
        errors[field.key] = `${field.label} deve conter URLs válidas`;
        return;
      }
    }

    if (!field.required) return;

    if (field.type === 'slides') {
      const slides = value || [];
      if (
        !Array.isArray(slides) ||
        slides.length === 0 ||
        slides.some((slide: any) => !hasValidHTMLContent(slide.description || ''))
      ) {
        errors[field.key] =
          `${field.label} é obrigatório e deve conter pelo menos um slide com descrição`;
      }
    } else if (field.type === 'highlights') {
      const items = value || [];
      if (
        !Array.isArray(items) ||
        items.length === 0 ||
        items.some(
          (item: any) =>
            !item?.title?.trim() ||
            !hasValidHTMLContent(item?.description || '')
        )
      ) {
        errors[field.key] =
          `${field.label} é obrigatório e deve conter pelo menos um item com título e descrição`;
      }
    } else if (field.type === 'social') {
      const socials = value || [];
      if (
        !Array.isArray(socials) ||
        socials.length === 0 ||
        socials.some(
          (item: any) =>
            !item?.type ||
            !['instagram', 'linkedin', 'youtube', 'twitter', 'tiktok'].includes(item.type)
        )
      ) {
        errors[field.key] =
          `${field.label} é obrigatório e deve conter ao menos uma rede social`;
        return;
      }

      if (
        Array.isArray(socials) &&
        socials.some((item: any) => item.link && !isValidUrl(String(item.link).trim()))
      ) {
        errors[field.key] = `${field.label} deve conter URLs válidas`;
      }
    } else if (field.type === 'bullets') {
      const bullets = value || [];
      if (
        !Array.isArray(bullets) ||
        bullets.length === 0 ||
        bullets.some((item: string) => !hasValidHTMLContent(item || ''))
      ) {
        errors[field.key] =
          `${field.label} é obrigatório e deve conter pelo menos um item preenchido`;
      }
    } else if (field.type === 'rich-text') {
      if (!hasValidHTMLContent(value || '')) {
        errors[field.key] = `${field.label} é obrigatório`;
      }
    } else if (field.type === 'date') {
      const val = (value ?? '').toString().trim();
      if (!val || !isValidReportDateString(val)) {
        errors[field.key] = `${field.label} é obrigatório e deve ser uma data válida`;
      }
    } else if (field.type !== 'links' && field.type !== 'logo-links') {
      if (!value || String(value).trim() === '') {
        errors[field.key] = `${field.label} é obrigatório`;
      }
    }
  });

  return errors;
}
